import { FlaskConical, Mic, Pencil, Plus, Power, RefreshCw, Save, Send, Sparkles, Trash2, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";
import { AutoAdvanceEditor, AutoAdvanceRow } from "@/components/AutoAdvance";
import { TransitionsPanel } from "@/components/TransitionsPanel";
import { VariantsEditor } from "@/components/VariantsEditor";
import { VoiceInstructionPanel } from "@/components/VoiceInstructionPanel";
import { useSearchParams } from "react-router-dom";
import { WebhookHealthBanner } from "@/components/WebhookHealthBanner";
import { BackfillPanel } from "@/components/BackfillPanel";
import { AiEmailDialog } from "@/components/AiEmailDialog";
import "@/automations.css";
import "@/inbox.css";

const emptyForm = {
  name: "",
  trigger_step_id: "",
  trigger_step_name: "",
  email_subject: "",
  email_body: "",
  attach_pdf: true,
  is_active: true,
  variants: [],
  auto_advance_enabled: false,
  auto_advance_days: 3,
  auto_advance_step_id: null,
  auto_advance_step_name: "",
};

const statusLabels = { envoye: "Envoyé", echec: "Échec", ignore: "Ignoré" };

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export default function AutomationsPage() {
  const [rules, setRules] = useState(null);
  const [variables, setVariables] = useState([]);
  const [logs, setLogs] = useState([]);
  const [steps, setSteps] = useState([]);
  const [transitionsKey, setTransitionsKey] = useState(0);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const target = searchParams.get("edit");
    if (target && Array.isArray(rules) && rules.length) {
      const rule = rules.find((item) => item.id === target);
      if (rule) { openEdit(rule); window.scrollTo({ top: 0 }); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules]);

  const rewriteWithAi = async (instruction) => {
    const { data } = await api.post("/relance-path/rewrite", { email_subject: form.email_subject, email_body: form.email_body, instruction }, { timeout: 120000 });
    setForm((current) => ({ ...current, email_subject: data.email_subject, email_body: data.email_body }));
    setVoiceOpen(false);
    toast.success("E-mail réécrit selon votre consigne — relisez avant d’enregistrer.");
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingRuleId, setTestingRuleId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [progress, setProgress] = useState(null);
  const [total, setTotal] = useState(0);
  const [displayLimit, setDisplayLimit] = useState(50);
  const [signature, setSignature] = useState(null);

  const loadProgress = async () => {
    const { data } = await api.get("/automations/retry-progress");
    setProgress(data.batch);
  };

  const loadLogs = async (filter, limit = displayLimit) => {
    const { data } = await api.get("/automations/logs", { params: { limit, ...(filter ? { status: filter } : {}) } });
    setLogs(data.logs);
    setTotal(data.total);
    setStats(data.stats);
  };

  const loadAll = async () => {
    const { data: automations } = await api.get("/automations");
    setRules(automations.rules);
    setVariables(automations.variables);
    await loadLogs(statusFilter);
    await loadProgress().catch(() => {});
  };

  useEffect(() => {
    loadAll().catch(() => toast.error("Impossible de charger les automatisations."));
    api
      .get("/settings/nocrm/steps")
      .then(({ data }) => {
        const climSteps = data.steps.filter((step) => (step.pipeline?.name || "").trim().toLowerCase() === "climatiseurs edf");
        setSteps(climSteps.length ? climSteps : data.steps);
      })
      .catch(() => toast.error("Étapes noCRM indisponibles : vérifiez la connexion noCRM dans Paramètres."));
    api
      .get("/settings")
      .then(({ data }) => setSignature(data.quote || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      loadLogs(statusFilter, displayLimit).catch(() => {});
      loadProgress().catch(() => {});
      api.get("/automations").then(({ data }) => setRules(data.rules)).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, displayLimit]);

  const openCreate = () => {
    setEditing("new");
    setForm(emptyForm);
  };

  const openEdit = (rule) => {
    setEditing(rule.id);
    setForm({
      name: rule.name,
      trigger_step_id: rule.trigger_step_id,
      trigger_step_name: rule.trigger_step_name,
      email_subject: rule.email_subject,
      email_body: rule.email_body,
      attach_pdf: rule.attach_pdf,
      is_active: rule.is_active,
      variants: rule.variants || [],
      auto_advance_enabled: Boolean(rule.auto_advance_enabled),
      auto_advance_days: rule.auto_advance_days || 3,
      auto_advance_step_id: rule.auto_advance_step_id || null,
      auto_advance_step_name: rule.auto_advance_step_name || "",
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    if (!form.name.trim() || !form.trigger_step_id || !form.email_subject.trim() || !form.email_body.trim()) {
      toast.error("Renseignez le nom, l’étape déclencheuse, l’objet et le corps de l’e-mail.");
      return;
    }
    setSaving(true);
    if (form.auto_advance_enabled && !form.auto_advance_step_id) {
      toast.error("Choisissez l’étape de destination du passage automatique.");
      return;
    }
    const payload = { ...form, trigger_step_id: Number(form.trigger_step_id), auto_advance_days: Number(form.auto_advance_days) || 3, variants: (form.variants || []).filter((variant) => variant.email_body.trim()) };
    try {
      const { data } =
        editing === "new" ? await api.post("/automations", payload) : await api.put(`/automations/${editing}`, payload);
      if (data.webhook_error) toast.warning(`Règle enregistrée, mais webhook noCRM non créé : ${data.webhook_error}`);
      else toast.success(editing === "new" ? "Automatisation créée et branchée sur noCRM." : "Automatisation mise à jour.");
      closeEditor();
      await loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’enregistrement de la règle a échoué.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      const { data } = await api.put(`/automations/${rule.id}`, {
        name: rule.name,
        trigger_step_id: rule.trigger_step_id,
        trigger_step_name: rule.trigger_step_name,
        email_subject: rule.email_subject,
        email_body: rule.email_body,
        attach_pdf: rule.attach_pdf,
        is_active: !rule.is_active,
        variants: rule.variants || [],
        auto_advance_enabled: Boolean(rule.auto_advance_enabled),
        auto_advance_days: rule.auto_advance_days || 3,
        auto_advance_step_id: rule.auto_advance_step_id || null,
        auto_advance_step_name: rule.auto_advance_step_name || "",
      });
      if (data.webhook_error) toast.warning(`Webhook noCRM non créé : ${data.webhook_error}`);
      else toast.success(data.is_active ? "Automatisation activée." : "Automatisation désactivée.");
      await loadAll();
    } catch {
      toast.error("Le changement d’état a échoué.");
    }
  };

  const removeRule = async (rule) => {
    if (!window.confirm(`Supprimer l’automatisation « ${rule.name} » ? Le webhook noCRM associé sera retiré.`)) return;
    try {
      await api.delete(`/automations/${rule.id}`);
      toast.success("Automatisation supprimée.");
      await loadAll();
    } catch {
      toast.error("La suppression a échoué.");
    }
  };

  const sendTest = async (ruleId) => {
    if (!testEmail.trim()) {
      toast.error("Saisissez une adresse e-mail de test.");
      return;
    }
    setTestingRuleId(ruleId);
    try {
      const { data } = await api.post(`/automations/${ruleId}/test`, { email: testEmail.trim() });
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’envoi du test a échoué.");
    } finally {
      setTestingRuleId(null);
    }
  };

  const changeFilter = async (filter) => {
    setStatusFilter(filter);
    setDisplayLimit(50);
    await loadLogs(filter, 50).catch(() => toast.error("Impossible de charger l’historique."));
  };

  const loadMore = async () => {
    const nextLimit = displayLimit + 50;
    setDisplayLimit(nextLimit);
    await loadLogs(statusFilter, nextLimit).catch(() => toast.error("Impossible de charger la suite de l’historique."));
  };

  const retryOne = async (logId) => {
    try {
      const { data } = await api.post(`/automations/logs/${logId}/retry`);
      toast.success(data.message);
      await loadLogs(statusFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || "La relance a échoué.");
    }
  };

  const retryAllFailures = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post("/automations/logs/retry-failures");
      toast.success(data.message);
      await loadLogs(statusFilter);
      await loadProgress().catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || "La relance groupée a échoué.");
    } finally {
      setRetrying(false);
    }
  };

  const insertVariable = (key) => {
    setForm((current) => ({ ...current, email_body: `${current.email_body}{${key}}` }));
  };

  if (!rules) return <section className="page">Chargement des automatisations…</section>;

  return (
    <section className="page" data-testid="automations-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">AUTOMATISATIONS</p>
          <h1>E-mails automatiques</h1>
          <p className="page-subtitle">Dès qu’un lead entre dans l’étape choisie, l’e-mail correspondant part immédiatement via Gmail.</p>
        </div>
        <button className="primary-button" onClick={openCreate} data-testid="create-automation-button">
          <Plus size={18} />Nouvelle règle
        </button>
      </header>

      <WebhookHealthBanner />

      <BackfillPanel />


      {editing && (
        <section className="settings-section automation-editor" data-testid="automation-editor">
          <div className="section-title">
            <Zap size={20} />
            <div>
              <p className="eyebrow">{editing === "new" ? "NOUVELLE RÈGLE" : "MODIFICATION"}</p>
              <h2>{editing === "new" ? "Créer une automatisation" : "Modifier l’automatisation"}</h2>
            </div>
          </div>
          <div className="form-grid">
            <label>Nom de la règle
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Ex. Relance après devis envoyé"
                data-testid="automation-name-input"
              />
            </label>
            <label>Étape déclencheuse noCRM
              <select
                value={form.trigger_step_id}
                onChange={(event) => {
                  const step = steps.find((item) => String(item.id) === event.target.value);
                  setForm({ ...form, trigger_step_id: event.target.value, trigger_step_name: step ? step.name : "" });
                }}
                data-testid="automation-step-select"
              >
                <option value="">— Choisir une étape —</option>
                {steps.map((step) => (
                  <option key={step.id} value={step.id}>{step.name}</option>
                ))}
              </select>
            </label>
          </div>
          <label>Objet de l’e-mail
            <input
              value={form.email_subject}
              onChange={(event) => setForm({ ...form, email_subject: event.target.value })}
              placeholder="Ex. Votre dossier {commune} — devis {devis_numero}"
              data-testid="automation-subject-input"
            />
          </label>
          <div className="button-row">
            <button className="secondary-button ai-button" type="button" onClick={() => setAiOpen(true)} data-testid="open-ai-dialog-button">
              <Sparkles size={16} />Rédiger avec l’IA
            </button>
          </div>
          <label>Corps de l’e-mail
            <textarea
              rows={10}
              value={form.email_body}
              onChange={(event) => setForm({ ...form, email_body: event.target.value })}
              placeholder={"Bonjour,\n\nNous revenons vers vous concernant la commune de {commune}…\n\nBien cordialement,\n{signature_nom}"}
              data-testid="automation-body-textarea"
            />
          </label>
          <div className="variable-chips" data-testid="automation-variables">
            <p className="eyebrow">VARIABLES DISPONIBLES (CLIQUEZ POUR INSÉRER)</p>
            <div className="variable-chips-row">
              {variables.map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  className="variable-chip"
                  title={variable.label}
                  onClick={() => insertVariable(variable.key)}
                  data-testid={`variable-chip-${variable.key}`}
                >
                  {`{${variable.key}}`}
                </button>
              ))}
            </div>
          </div>
          {signature && (
            <div className="signature-hint" data-testid="signature-hint">
              <div className="signature-hint-head">
                <span className="eyebrow">SIGNATURE ACTUELLE (utilisée par {"{signature_nom}"}, {"{signature_titre}"}, {"{signature_telephone}"})</span>
                <a href="/devis" className="link-button" data-testid="signature-hint-edit">Modifier dans Paramètres devis</a>
              </div>
              <ul className="signature-hint-list">
                <li><span>Nom</span><strong data-testid="signature-hint-name">{signature.signature_name || "—"}</strong></li>
                <li><span>Titre</span><strong data-testid="signature-hint-title">{signature.signature_title || "—"}</strong></li>
                <li><span>Téléphone</span><strong data-testid="signature-hint-phone">{signature.signature_phone || "—"}</strong></li>
              </ul>
            </div>
          )}
          <div className="button-row">
            <button type="button" className="secondary-button" onClick={() => setVoiceOpen((open) => !open)} disabled={!form.email_body.trim()} data-testid="rule-voice-rewrite-button">
              <Mic size={14} /> Réorienter l’e-mail avec l’IA (micro)
            </button>
          </div>
          {voiceOpen && <VoiceInstructionPanel ticketId={editing === "new" ? "new" : editing} onSubmit={rewriteWithAi} onClose={() => setVoiceOpen(false)} submitLabel="Réécrire l’e-mail" />}
          <VariantsEditor form={form} setForm={setForm} />
          <AutoAdvanceEditor form={form} setForm={setForm} steps={steps} />
          <div className="check-row">
            <label className="automation-checkbox">
              <input
                type="checkbox"
                checked={form.attach_pdf}
                onChange={(event) => setForm({ ...form, attach_pdf: event.target.checked })}
                data-testid="automation-attach-pdf-checkbox"
              />
              Joindre le dernier devis PDF du dossier
            </label>
            <label className="automation-checkbox">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                data-testid="automation-active-checkbox"
              />
              Règle active
            </label>
          </div>
          <div className="button-row">
            <button className="primary-button" onClick={save} disabled={saving} data-testid="save-automation-button">
              <Save size={17} />{saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button className="secondary-button" type="button" onClick={closeEditor} data-testid="cancel-automation-button">
              <X size={17} />Annuler
            </button>
          </div>
        </section>
      )}

      {aiOpen && (
        <AiEmailDialog
          context="automation"
          onClose={() => setAiOpen(false)}
          onApply={(subject, body) => setForm((current) => ({ ...current, email_subject: subject, email_body: body }))}
        />
      )}

      <section className="settings-section" data-testid="automation-rules-list">
        <div className="section-title">
          <Zap size={20} />
          <div><p className="eyebrow">RÈGLES</p><h2>{rules.length} automatisation{rules.length > 1 ? "s" : ""}</h2></div>
        </div>
        {rules.length === 0 && (
          <p className="source-note" data-testid="automations-empty-state">
            Aucune règle pour le moment. Créez votre première automatisation : choisissez une étape noCRM, rédigez l’e-mail, et l’envoi devient automatique.
          </p>
        )}
        {rules.map((rule) => (
          <article key={rule.id} className="automation-card" data-testid={`automation-card-${rule.id}`}>
            <div className="automation-card-head">
              <div>
                <h3 data-testid={`automation-card-name-${rule.id}`}>{rule.name}</h3>
                <p className="source-note">
                  Étape déclencheuse : <strong>{rule.trigger_step_name || rule.trigger_step_id}</strong>
                  {rule.attach_pdf ? " · devis PDF joint" : ""}{rule.variants?.length ? ` · ${rule.variants.length} variante(s)` : ""}
                </p>
              </div>
              <span className={`status-badge ${rule.is_active ? "status-termine" : "status-en"}`} data-testid={`automation-status-${rule.id}`}>
                {rule.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <p className="automation-subject" data-testid={`automation-subject-${rule.id}`}>Objet : {rule.email_subject}</p>
            {rule.webhook_error && rule.is_active && (
              <p className="review-alert" data-testid={`automation-webhook-error-${rule.id}`}>
                Webhook noCRM non branché : {rule.webhook_error} Utilisez « Vérifier & réparer le webhook » dans Paramètres ou réenregistrez la règle.
              </p>
            )}
            <p className="source-note">
              {rule.trigger_count || 0} envoi{(rule.trigger_count || 0) > 1 ? "s" : ""} · dernier déclenchement : {formatDate(rule.last_triggered_at)}
            </p>
            <AutoAdvanceRow rule={rule} onDone={() => setTransitionsKey((k) => k + 1)} />
            <div className="button-row automation-actions">
              <button className="secondary-button" type="button" onClick={() => toggleActive(rule)} data-testid={`toggle-automation-${rule.id}`}>
                <Power size={15} />{rule.is_active ? "Désactiver" : "Activer"}
              </button>
              <button className="secondary-button" type="button" onClick={() => openEdit(rule)} data-testid={`edit-automation-${rule.id}`}>
                <Pencil size={15} />Modifier
              </button>
              <button className="secondary-button danger" type="button" onClick={() => removeRule(rule)} data-testid={`delete-automation-${rule.id}`}>
                <Trash2 size={15} />Supprimer
              </button>
            </div>
            <div className="automation-test-row">
              <FlaskConical size={15} />
              <input
                type="email"
                value={testEmail}
                onChange={(event) => setTestEmail(event.target.value)}
                placeholder="adresse@test.fr"
                data-testid={`test-email-input-${rule.id}`}
              />
              <button
                className="secondary-button"
                type="button"
                disabled={testingRuleId === rule.id}
                onClick={() => sendTest(rule.id)}
                data-testid={`send-test-automation-${rule.id}`}
              >
                <Send size={15} />{testingRuleId === rule.id ? "Envoi…" : "Envoyer un test"}
              </button>
            </div>
          </article>
        ))}
      </section>

      <TransitionsPanel refreshKey={transitionsKey} />

      <section className="settings-section" data-testid="automation-logs-section">
        <div className="section-title">
          <Send size={20} />
          <div><p className="eyebrow">HISTORIQUE</p><h2>Derniers envois automatiques</h2></div>
        </div>
        {stats && (
          <div className="stats-grid automation-stats" data-testid="automation-stats">
            <article className="stat-box" data-testid="stat-emails-envoyes"><span>E-mails envoyés</span><strong>{stats.envoye}</strong></article>
            <article className="stat-box" data-testid="stat-emails-echecs" title={`${stats.echec_total} échec(s) au total dans l’historique, dont ${stats.echec_total - stats.a_relancer} déjà relancé(s)`}>
              <span>Échecs restants à traiter</span><strong>{stats.a_relancer}</strong>
            </article>
            <article className="stat-box" data-testid="stat-emails-ignores"><span>Ignorés</span><strong>{stats.ignore}</strong></article>
          </div>
        )}
        {progress && progress.status === "en_cours" && (
          <div className="retry-progress" data-testid="retry-progress-banner">
            <div className="retry-progress-track"><div className="retry-progress-fill" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} /></div>
            <p data-testid="retry-progress-text">
              Relance en cours : <strong>{progress.done}/{progress.total}</strong> traités — {progress.success} réussi{progress.success > 1 ? "s" : ""}, {progress.failed} échec{progress.failed > 1 ? "s" : ""} · mise à jour automatique
            </p>
          </div>
        )}
        {progress && progress.status === "termine" && Date.now() - new Date(progress.finished_at).getTime() < 15 * 60 * 1000 && (
          <p className="retry-progress-done" data-testid="retry-progress-done">
            Dernière relance groupée terminée ({formatDate(progress.finished_at)}) : {progress.success} réussi{progress.success > 1 ? "s" : ""} sur {progress.total}, {progress.failed} échec{progress.failed > 1 ? "s" : ""}.
          </p>
        )}
        {progress && progress.status === "interrompu" && (
          <p className="review-alert" data-testid="retry-progress-interrupted">
            La dernière relance groupée a été interrompue à {progress.done}/{progress.total}. Les échecs restants réapparaissent dans le compteur : relancez-les à nouveau.
          </p>
        )}
        <div className="button-row automation-log-controls">
          {[["", "Tous"], ["envoye", "Envoyés"], ["echec", "Échecs"], ["ignore", "Ignorés"]].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`secondary-button ${statusFilter === value ? "filter-active" : ""}`}
              onClick={() => changeFilter(value)}
              data-testid={`log-filter-${value || "tous"}`}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="primary-button"
            onClick={retryAllFailures}
            disabled={retrying || !stats || stats.a_relancer === 0}
            data-testid="retry-all-failures-button"
          >
            <RefreshCw size={15} className={retrying ? "spinning" : ""} />
            {retrying ? "Relance en cours…" : `Relancer tous les échecs${stats && stats.a_relancer > 0 ? ` (${stats.a_relancer})` : ""}`}
          </button>
        </div>
        {logs.length === 0 ? (
          <p className="source-note" data-testid="automation-logs-empty">
            {statusFilter ? "Aucun envoi ne correspond à ce filtre." : "Aucun envoi automatique pour le moment."}
          </p>
        ) : (
          <>
            <p className="table-count" data-testid="automation-logs-count">
              {logs.length} ligne{logs.length > 1 ? "s" : ""} affichée{logs.length > 1 ? "s" : ""} sur {total}
              {!statusFilter ? " — les anciens échecs déjà relancés sont masqués (visibles via le filtre « Échecs »)" : ""}
            </p>
            <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Date</th><th>Règle</th><th>Lead</th><th>Destinataire</th><th>Statut</th><th>Détail</th><th>Action</th></tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} data-testid={`automation-log-${log.id}`}>
                    <td>{formatDate(log.created_at)}</td>
                    <td>{log.rule_name || "—"}</td>
                    <td>
                      {log.lead_url ? (
                        <a href={log.lead_url} target="_blank" rel="noreferrer" className="lead-link" title="Ouvrir la fiche dans noCRM" data-testid={`lead-link-${log.id}`}>
                          {log.lead_title || log.lead_id}
                        </a>
                      ) : (
                        log.lead_title || log.lead_id
                      )}
                    </td>
                    <td>{log.recipient_email || "—"}</td>
                    <td><span className={`status-badge ${log.status === "envoye" ? "status-termine" : log.status === "echec" ? "status-erreur" : "status-en"}`}>{statusLabels[log.status] || log.status}</span></td>
                    <td className="automation-log-reason">{log.reason || "—"}</td>
                    <td>
                      {log.status === "echec" && !log.retried_at && (
                        <button className="secondary-button" type="button" onClick={() => retryOne(log.id)} data-testid={`retry-log-${log.id}`}>
                          <RefreshCw size={14} />Relancer
                        </button>
                      )}
                      {log.status === "echec" && log.retried_at && (
                        <span className="status-badge status-en" title={`Relancé le ${formatDate(log.retried_at)}`} data-testid={`retried-badge-${log.id}`}>Relancé</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            {logs.length < total && (
              <div className="load-more-row">
                <button className="secondary-button" type="button" onClick={loadMore} data-testid="load-more-logs-button">
                  Afficher plus d’historique ({total - logs.length} ligne{total - logs.length > 1 ? "s" : ""} encore masquée{total - logs.length > 1 ? "s" : ""})
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </section>
  );
}
